import gsap from "gsap";

class StartHero {
    private container: HTMLElement;
    private textContainer: HTMLElement | null;
    private textContent1: HTMLElement | null;
    private textContent2: HTMLElement | null;
    private textInners: HTMLElement[];
    private scrollArrows: HTMLElement[];
    private resizeHandler: () => void;
    private lastIsLargeScreen: boolean;

    constructor(element: HTMLElement) {
        this.container = element;

        this.textContainer = this.container.querySelector('.start-hero__text-container');
        this.textContent1 = this.container.querySelector('.start-hero__text-content-1');
        this.textContent2 = this.container.querySelector('.start-hero__text-content-2');
        this.textInners = Array.from(this.container.querySelectorAll<HTMLElement>('.start-hero__text-inner'));
        this.scrollArrows = Array.from(this.container.querySelectorAll<HTMLElement>('.start-hero__scroll-arrow'));
        gsap.set(this.scrollArrows, { opacity: 0 });

        this.lastIsLargeScreen = window.innerWidth >= 1024;

        if (this.textContainer) {
            this.textContainer.classList.remove('hidden');
        }

        this.resizeHandler = () => this.handleResize();
        window.addEventListener('resize', this.resizeHandler);

        this.fitTextElements();
        this.animateEntry();
        this.initTextAnimation();
        window.setTimeout(() => this.initScrollIndicator(), 6000);
    }

    private handleResize() {
        this.fitTextElements();
        const isLargeScreen = window.innerWidth >= 1024;
        if (isLargeScreen !== this.lastIsLargeScreen) {
            this.lastIsLargeScreen = isLargeScreen;
            this.animateEntry();
        }
    }

    private fitText(el: HTMLElement) {
        const textContainer = el.closest<HTMLElement>('.start-hero__text-container');
        const textWrapper = el.parentElement;
        const wrapperStyle = textWrapper ? getComputedStyle(textWrapper) : null;
        const wrapperPaddingV = wrapperStyle
            ? parseFloat(wrapperStyle.paddingTop) + parseFloat(wrapperStyle.paddingBottom)
            : 0;
        const availableHeight = textContainer
            ? textContainer.clientHeight - wrapperPaddingV
            : Infinity;

        // Temporarily suppress word-wrapping so scrollWidth reflects the widest line
        el.style.whiteSpace = 'nowrap';
        el.style.fontSize = '';

        let lo = 8, hi = 400;
        while (hi - lo > 0.5) {
            const mid = (lo + hi) / 2;
            el.style.fontSize = `${mid}px`;
            const fitsWidth = el.scrollWidth <= el.offsetWidth;
            const fitsHeight = el.scrollHeight <= availableHeight;
            if (fitsWidth && fitsHeight) {
                lo = mid;
            } else {
                hi = mid;
            }
        }

        el.style.fontSize = `${lo}px`;
        // Keep nowrap — only explicit <br> tags may break lines, no word-wrapping
    }

    private fitTextElements() {
        this.textInners.forEach(el => this.fitText(el));
    }

    private animateEntry() {
        if (!this.textContainer) return;

        const isLargeScreen = window.innerWidth >= 1024;

        gsap.fromTo(this.textContainer,
            { x: isLargeScreen ? '-100%' : 0, y: isLargeScreen ? 0 : '100%' },
            { x: 0, y: 0, duration: 0.8, ease: 'power2.out', delay: 0.2 }
        );
    }

    private initTextAnimation() {
        if (!this.textContent1 || !this.textContent2) return;

        gsap.set(this.textContent1, { opacity: 0, y: 30 });
        gsap.set(this.textContent2, { opacity: 0, y: 30 });

        const tl = gsap.timeline({ repeat: -1, delay: 0.8 });

        // text1: fade in, hold, fade out
        tl.to(this.textContent1, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' });
        tl.to(this.textContent1, { opacity: 1, duration: 3.5 });
        tl.to(this.textContent1, { opacity: 0, y: -30, duration: 0.5, ease: 'power2.in' });

        tl.set(this.textContent1, { y: 30 });
        tl.to({}, { duration: 0.4 }); // brief pause between texts

        // text2: fade in, hold, fade out
        tl.to(this.textContent2, { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out' });
        tl.to(this.textContent2, { opacity: 1, duration: 3.5 });
        tl.to(this.textContent2, { opacity: 0, y: -30, duration: 0.5, ease: 'power2.in' });

        tl.set(this.textContent2, { y: 30 });
        tl.to({}, { duration: 0.4 }); // brief pause before looping back
    }

    private initScrollIndicator() {
        if (!this.scrollArrows.length) return;

        gsap.set(this.scrollArrows, { opacity: 0, y: -6 });

        const tl = gsap.timeline({ repeat: -1, delay: 1.2 });

        // Cascade each arrow in with a stagger, then fade all out together
        tl.to(this.scrollArrows, {
            opacity: 1,
            y: 0,
            duration: 0.4,
            ease: 'power2.out',
            stagger: 0.2,
        });
        tl.to({}, { duration: 0.8 }); // hold
        tl.to(this.scrollArrows, {
            opacity: 0,
            y: 6,
            duration: 0.4,
            ease: 'power2.in',
            stagger: 0.15,
        });
        tl.set(this.scrollArrows, { y: -6 });
        tl.to({}, { duration: 0.6 }); // pause before next loop
    }

    public destroy() {
        window.removeEventListener('resize', this.resizeHandler);
        gsap.killTweensOf([this.textContent1, this.textContent2, this.textContainer, ...this.scrollArrows]);
    }
}

document.addEventListener("DOMContentLoaded", () => {
    const heroElements = document.querySelectorAll(".start-hero") as NodeListOf<HTMLElement>;
    heroElements.forEach((heroEl) => {
        new StartHero(heroEl);
    });
});

export default StartHero;
